import React, { useCallback, useMemo } from 'react';
import { usePeriodLogic } from '../../hooks/usePeriodLogic';

const SelectField = ({ label, value, onChange, disabled, options, placeholder }) => {
  const selectClass = "w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] transition-all duration-300 outline-none appearance-none bg-white shadow-sm disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60";

  return (
    <div className="space-y-3">
      <label className="block text-sm font-semibold text-[#09274A]">
        {label} *
      </label>
      <div className="relative">
        <select
          className={selectClass}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export const PeriodSelector = ({ 
  availableYears = [],
  selectedYear = '',
  reportType = '',
  selectedPeriod = '',
  onYearChange,
  onReportTypeChange,
  onPeriodChange,
  disabled = false,
  // Configuración de campos visibles
  showYear = true,
  showReportType = true,
  showPeriod = true,
  // Configuración personalizada
  defaultReportType = null, // Para usar un tipo fijo sin mostrar el selector
  periodMode = 'auto' // 'auto', 'monthly', 'biannual', 'annual'
}) => {
  const { REPORT_TYPES, getPeriodOptions } = usePeriodLogic();

  // Si hay un tipo de reporte por defecto, usarlo
  const effectiveReportType = defaultReportType || reportType;

  const yearOptions = useMemo(() => 
    availableYears.map(year => ({ value: year, label: year.toString() })),
    [availableYears]
  );

  const reportTypeOptions = useMemo(() => [
    { value: REPORT_TYPES.MONTHLY, label: "📅 Mensual" },
    { value: REPORT_TYPES.BIANNUAL, label: "📊 Semestral" },
    { value: REPORT_TYPES.ANNUAL, label: "📈 Anual" }
  ], [REPORT_TYPES]);

  // Opciones de período basadas en el modo
  const periodOptions = useMemo(() => {
    if (periodMode !== 'auto') {
      // Modo específico forzado
      return getPeriodOptions(periodMode, selectedYear);
    }
    // Modo automático basado en reportType
    return getPeriodOptions(effectiveReportType, selectedYear);
  }, [getPeriodOptions, effectiveReportType, selectedYear, periodMode]);

  const handleYearChange = useCallback((year) => {
    onYearChange?.(year);
  }, [onYearChange]);

  const handleReportTypeChange = useCallback((type) => {
    onReportTypeChange?.(type);
  }, [onReportTypeChange]);

  const handlePeriodChange = useCallback((period) => {
    onPeriodChange?.(period);
  }, [onPeriodChange]);

  // Calcular el número de columnas dinámicamente
  const visibleFields = [showYear, showReportType, showPeriod].filter(Boolean).length;
  const gridCols = visibleFields === 1 ? 'grid-cols-1' : 
                   visibleFields === 2 ? 'grid-cols-1 md:grid-cols-2' : 
                   'grid-cols-1 md:grid-cols-3';

  return (
    <div className={`grid ${gridCols} gap-6`}>
      {showYear && (
        <SelectField
          label="Año"
          value={selectedYear}
          onChange={handleYearChange}
          disabled={disabled}
          options={yearOptions}
          placeholder="-- Selecciona año --"
        />
      )}
      
      {showReportType && !defaultReportType && (
        <SelectField
          label="Tipo de reporte"
          value={reportType}
          onChange={handleReportTypeChange}
          disabled={disabled || !selectedYear}
          options={reportTypeOptions}
          placeholder={!selectedYear ? "-- Primero selecciona un año --" : "-- Selecciona tipo --"}
        />
      )}
      
      {showPeriod && (
        <SelectField
          label="Periodo"
          value={selectedPeriod}
          onChange={handlePeriodChange}
          disabled={disabled || (!effectiveReportType && periodMode === 'auto')}
          options={periodOptions}
          placeholder={
            periodMode === 'auto' && !effectiveReportType 
              ? "-- Selecciona el tipo primero --" 
              : "-- Selecciona periodo --"
          }
        />
      )}
    </div>
  );
};